import React, { useMemo } from 'react'

import PropTypes from 'prop-types'
import styled from 'styled-components'
import { twMerge } from 'tailwind-merge'

import { Column, Row } from '../../v2/Grid'
import { Text } from '../../v2/Text'

const Input = ({
	value: valueProp,
	register,
	type,
	label,
	placeholder,
	error,
	disabled,
	name,
	input,
	labelProps,
	inputTextProps,
	variant,
	icon,
	defaultValue,
	containerStyle,
	containerClassName,
	className,
	maxLength,
	onChange: onChangeProp,
	onBlur: onBlurProp,
	...props
}) => {
	const top = error ? '3px' : '12px'

	const inputProps = useMemo(() => {
		if (input) {
			return {
				name: input.name,
				value: input.value ?? '',
				onChange: input.onChange,
				onBlur: input.onBlur,
				ref: null
			}
		}

		if (register && typeof register === 'object' && 'ref' in register) {
			return {
				name: register.name || name,
				value: register.value ?? valueProp ?? defaultValue ?? '',
				onChange: register.onChange || onChangeProp,
				onBlur: register.onBlur || onBlurProp,
				ref: register.ref
			}
		}

		if (register && typeof register === 'function') {
			return {
				name: name,
				value: valueProp ?? defaultValue ?? '',
				onChange: onChangeProp,
				onBlur: onBlurProp,
				ref: register
			}
		}

		return {
			name: name,
			value: valueProp ?? defaultValue ?? '',
			onChange: onChangeProp,
			onBlur: onBlurProp,
			ref: null
		}
	}, [register, input, name, valueProp, defaultValue, onChangeProp, onBlurProp])

	if (variant === 'primary') {
		return (
			<Column
				className={twMerge('w-full items-start', containerClassName)}
				{...containerStyle}
			>
				<PrimaryInput
					disabled={disabled}
					error={error}
					className={className}
					{...props}
				>
					<Row className='w-full items-center justify-between'>
						<Column className='w-full items-start'>
							<Text
								className='!pt-1 text-[10px] text-orange-600'
								{...labelProps}
							>
								{label}
							</Text>
							<PrimaryInputText
								data-testid={name}
								color='black'
								name={inputProps.name}
								type={type}
								error={error}
								disabled={disabled}
								ref={inputProps.ref}
								value={inputProps.value}
								onChange={inputProps.onChange}
								onBlur={inputProps.onBlur}
								placeholder={placeholder}
								defaultValue={defaultValue}
								maxLength={maxLength}
							/>
						</Column>
						{icon && icon}
					</Row>
				</PrimaryInput>
				{error && (
					<Text className='text-left text-[9px] text-red-700 md:text-[11px]'>
						{error}
					</Text>
				)}
			</Column>
		)
	}

	return (
		<InputField
			disabled={disabled}
			error={error}
			className={className}
			{...props}
		>
			<InputText
				color='black'
				name={inputProps.name}
				type={type}
				error={error}
				disabled={disabled}
				ref={inputProps.ref}
				placeholder={placeholder}
				value={inputProps.value}
				onChange={inputProps.onChange}
				onBlur={inputProps.onBlur}
				defaultValue={defaultValue}
				{...inputTextProps}
			/>
			<FloatingLabel
				className='text-gray-700'
				{...labelProps}
				as='label'
				register={register}
				value={inputProps.value || defaultValue}
				top={top}
			>
				{error && (
					<Text className='text-left text-[9px] text-red-700 md:text-[11px]'>
						{error}
					</Text>
				)}
				{label}
			</FloatingLabel>
		</InputField>
	)
}

const PrimaryInput = styled(Column)(
	({ theme: { colors }, error, disabled }) => `
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  border: 1px solid ${(error && colors.red) || (disabled && colors.gray.n200) || '#E7EAF4'};
  height: 48px;
  width: 100%;
  margin: 0 0 6px 0;
  justify-content: center;
  align-items: flex-start;
  border-radius: 4px;
  padding-right: 12px;
  padding-left: 12px;
`
)

const PrimaryInputText = styled.input`
	font-size: 14px;
	width: 100%;
	background-color: transparent;
	font-family: Fira Sans;
	&::placeholder {
		font-size: 14px !important;
	}
`

const InputField = styled.label(
	({ theme: { colors }, error, disabled }) => `
  background-color: ${(disabled && colors.gray.n200) || colors.white};
  position: relative;
  border: 0.5px solid ${(error && colors.red) || (disabled && colors.gray.n200) || '#E7EAF4'};
  padding: 20px 12px 0 12px;
  height: 48px;
  margin-bottom: 18px;
  border-radius: 6px;
`
)

const InputText = styled.input`
	border: none;
	font-size: 16px;
	width: 100%;
	background-color: transparent;
	color: black;
`

const FloatingLabel = styled(Text)`
	position: absolute;
	pointer-events: none;
	left: 12px;
	transition: 0.3s ease all;

	${InputText}:focus ~ &,
  ${({ value }) => !value} && {
		top: 5px;
		left: 12px;
		font-size: 12px;
	}
`

Input.defaultProps = {
	disabled: false
}

Input.propTypes = {
	disabled: PropTypes.bool,
	error: PropTypes.string,
	type: PropTypes.string,
	name: PropTypes.string,
	label: PropTypes.string,
	labelProps: PropTypes.object,
	inputTextProps: PropTypes.object,
	variant: PropTypes.string
}

export { Input }
